import { useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { medicationsService, type CreateMedicationData } from '@/services/api/medications.service';
import type { Medication } from '@/types/consultation';
import { MedicationSearchModal } from './MedicationSearchModal';

export interface SelectedMedication {
  medication: Medication;
  durationDays: number;
  instructions: string;
}

interface MedicationManagerProps {
  selectedMedications: SelectedMedication[];
  onMedicationsChange: (medications: SelectedMedication[]) => void;
  className?: string;
}

export function MedicationManager({
  selectedMedications,
  onMedicationsChange,
  className = '',
}: MedicationManagerProps) {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showMedicationForm, setShowMedicationForm] = useState(false);
  const [newMedication, setNewMedication] = useState<CreateMedicationData>({
    name: '',
    dosageMg: 0,
    frequencyHours: 8,
  });

  const handleAddMedication = (medication: Medication) => {
    // Check if already added
    if (selectedMedications.some((sm) => sm.medication.id === medication.id)) {
      toast.error('Medicamento já adicionado');
      return;
    }

    onMedicationsChange([
      ...selectedMedications,
      {
        medication,
        durationDays: 7,
        instructions: '',
      },
    ]);
    setShowSearchModal(false);
  };

  const handleRemoveMedication = (medicationId: string) => {
    onMedicationsChange(
      selectedMedications.filter((sm) => sm.medication.id !== medicationId)
    );
  };

  const handleUpdateMedication = (
    medicationId: string,
    field: 'durationDays' | 'instructions',
    value: number | string
  ) => {
    onMedicationsChange(
      selectedMedications.map((sm) =>
        sm.medication.id === medicationId
          ? { ...sm, [field]: value }
          : sm
      )
    );
  };

  const handleCreateNewMedication = async () => {
    if (!newMedication.name.trim()) {
      toast.error('Nome do medicamento é obrigatório');
      return;
    }

    if (newMedication.dosageMg <= 0) {
      toast.error('Dosagem deve ser maior que zero');
      return;
    }

    if (newMedication.frequencyHours <= 0) {
      toast.error('Frequência deve ser maior que zero');
      return;
    }

    try {
      const created = await medicationsService.create(newMedication);
      toast.success('Medicamento criado com sucesso!');
      handleAddMedication(created);
      setNewMedication({ name: '', dosageMg: 0, frequencyHours: 8 });
      setShowMedicationForm(false);
    } catch (error) {
      toast.error('Erro ao criar medicamento');
    }
  };

  return (
    <div className={className}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Medicamentos Prescritos</h3>

      {/* Selected Medications - Scrollable */}
      {selectedMedications.length > 0 && (
        <div className="mb-4 space-y-3 max-h-[300px] overflow-y-auto pr-2">
          {selectedMedications.map((sm) => (
            <div
              key={sm.medication.id}
              className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg"
            >
              <div className="flex-1">
                <div className="font-medium text-gray-900">{sm.medication.name}</div>
                <div className="text-sm text-gray-600">
                  {sm.medication.dosageMg}mg - A cada {sm.medication.frequencyHours}h
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Duração (dias)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={sm.durationDays}
                      onChange={(e) =>
                        handleUpdateMedication(
                          sm.medication.id,
                          'durationDays',
                          parseInt(e.target.value) || 1
                        )
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Instruções
                    </label>
                    <input
                      type="text"
                      value={sm.instructions}
                      onChange={(e) =>
                        handleUpdateMedication(
                          sm.medication.id,
                          'instructions',
                          e.target.value
                        )
                      }
                      placeholder="Ex: Tomar com alimentos"
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleRemoveMedication(sm.medication.id)}
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search Medications */}
      {!showMedicationForm && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setShowSearchModal(true)}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 font-medium"
            >
              <Search className="h-5 w-5" />
              Buscar Medicamento
            </button>
            <button
              onClick={() => setShowMedicationForm(true)}
              className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 font-medium"
            >
              <Plus className="h-5 w-5" />
              Novo
            </button>
          </div>
        </div>
      )}

      {/* New Medication Form */}
      {showMedicationForm && (
        <div className="p-4 bg-gray-50 rounded-lg space-y-3">
          <h4 className="font-medium text-gray-900">Novo Medicamento</h4>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Nome *</label>
            <input
              type="text"
              value={newMedication.name}
              onChange={(e) =>
                setNewMedication({ ...newMedication, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Nome do medicamento"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Dosagem (mg) *</label>
              <input
                type="number"
                min="1"
                value={newMedication.dosageMg || ''}
                onChange={(e) =>
                  setNewMedication({
                    ...newMedication,
                    dosageMg: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Ex: 500"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Frequência (horas) *
              </label>
              <input
                type="number"
                min="1"
                value={newMedication.frequencyHours || ''}
                onChange={(e) =>
                  setNewMedication({
                    ...newMedication,
                    frequencyHours: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Ex: 8"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowMedicationForm(false);
                setNewMedication({ name: '', dosageMg: 0, frequencyHours: 8 });
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateNewMedication}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Criar e Adicionar
            </button>
          </div>
        </div>
      )}

      {/* Search Modal */}
      {showSearchModal && (
        <MedicationSearchModal
          selectedMedicationIds={selectedMedications.map(sm => sm.medication.id)}
          onSelect={handleAddMedication}
          onClose={() => setShowSearchModal(false)}
        />
      )}
    </div>
  );
}
